import "./ProjectAccessTable.css";

import { EditGreyIcon } from "src/assets";

import { ProjectAccessConstants } from "../../../constants";
import { ResultData } from "../../../service/Shared";
import {
  CursorDataGrid,
  DataGridCellFormatter,
  DataGridCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
} from "../../data";
import { Button, Checkbox } from "../../forms";
import { ProjectAccessTableProps } from "./models";

type ResponsibleOwnerCellData = DataGridCellFormatterData & {
  organisationUuid: string;
  isResponsibleOwner: boolean;
  canBeResponsibleOwner: boolean;
  isEditing: boolean;
  onResponsibleOwnerChange: (organisationUuid: string) => void;
};

const responsibleOwnerCellFormatter: DataGridCellFormatter<ResponsibleOwnerCellData | null | undefined> = () => {
  return (value) => {
    if (!value) return <div />;

    const { organisationUuid, isResponsibleOwner, canBeResponsibleOwner, isEditing, onResponsibleOwnerChange } = value;

    return (
      <div className="DataGridCheckboxCell">
        <Checkbox
          checked={isResponsibleOwner}
          disabled={!isEditing || !canBeResponsibleOwner}
          onChange={() => onResponsibleOwnerChange(organisationUuid)}
        />
      </div>
    );
  };
};

const responsibleOwnerColumn: DataGridColumnDefinitionWithCustomCellFormatter<
  ResponsibleOwnerCellData | null | undefined
> = {
  key: "responsibleOwner",
  name: "Responsible owner",
  dataType: "boolean",
  formatter: "custom",
  customCellFormatter: responsibleOwnerCellFormatter,
  alignment: "center",
  filterable: false,
  sortable: false,
  minWidth: 48,
};

// No widths - the four columns share the grid equally, floored at minWidth.
const columns: DataGridColumnDefinition[] = [
  { key: "organisationType", name: "Organisation type", dataType: "string", minWidth: 48 },
  { key: "organisationName", name: "Organisation", dataType: "string", minWidth: 48 },
  { key: "access", name: "Access", dataType: "string", minWidth: 48 },
  responsibleOwnerColumn,
];

export const ProjectAccessTable = ({
  rows,
  onResponsibleOwnerChange,
  canEdit = false,
  isEditing = false,
  onEditClick,
  onSave,
  onCancel,
  isSaving = false,
  isLoading = false,
  hasError = false,
  onChange,
  variant = "default",
}: ProjectAccessTableProps): JSX.Element => {
  const data: ResultData[] = rows.map((row) => ({
    uuid: row.organisationUuid,
    organisationType: row.organisationType,
    organisationName: row.organisationName,
    access: ProjectAccessConstants.getAccessLevelLabel(row.role),
    responsibleOwner: {
      organisationUuid: row.organisationUuid,
      isResponsibleOwner: row.isResponsibleOwner,
      canBeResponsibleOwner: row.canBeResponsibleOwner,
      isEditing,
      onResponsibleOwnerChange,
    } as ResponsibleOwnerCellData,
  }));

  const header = (
    <>
      <div className="ProjectAccessTable_heading">
        <h2>Access</h2>
        <span className="body1">
          See which organisations can access this project and their permission levels. The responsible owner can invite
          new organisations to the project using the Invites table.
        </span>
      </div>
      {canEdit && !isEditing && <EditGreyIcon className="ProjectAccessTable_editIcon" onClick={onEditClick} />}
    </>
  );

  return (
    <div className="ProjectAccessTable">
      <CursorDataGrid
        columns={columns}
        data={data}
        header={header}
        clientSide={!onChange}
        onChange={onChange}
        sortable={!!onChange}
        filterable={!!onChange}
        pageable={!!onChange}
        showTotalCount={!!onChange}
        filterRowSpacingOverride="l"
        noDataMessage={
          hasError ? "Organisations could not be loaded. Please try again later." : "No organisations to display"
        }
        dataIsLoading={isLoading}
        variant={variant}
      />
      {isEditing && (
        <div className="ProjectAccessTable_actions">
          <Button
            text="Save"
            isLoading={isSaving}
            loadingText="Saving"
            corners={variant === "seed" ? "rounded" : "default"}
            onClick={onSave}
          />
          <Button
            text="Cancel"
            variant="secondary"
            corners={variant === "seed" ? "rounded" : "default"}
            onClick={onCancel}
          />
        </div>
      )}
    </div>
  );
};
